import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'results-energy-flow',
    category: 'results',
    title: 'Lire le graphique Bilan Énergétique',
    summary: 'Comment lire le graphique heure par heure qui montre chaque source et usage de l\'énergie après une optimisation.',
    body: (
      <>
        <p>
          Le graphique <strong>Bilan Énergétique</strong> apparaît sous le récapitulatif des KPI sur
          les résultats de la page Optimisation. C'est un graphique en aires horaire avec une série
          pour chaque type de source ou d'usage, basculable entre les vues à l'échelle de la
          communauté et par bâtiment.
        </p>
        <ScreenshotPlaceholder
          img="energy-flow.png"
          description="The Energy Balance area chart over 24 hours — stacked series for Base Load, Flexible Load, PV Generation (or Rooftop PV), Wind Generation, Grid Import, Surplus, Battery Charge and Battery Discharge."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Scroll below the KPI Summary to the Energy Balance chart and capture the full 24-hour area chart."
          caption="Bilan Énergétique sur la journée optimisée."
        />

        <h5 className="mt-3">Les séries affichées</h5>
        <p>Le graphique peut afficher les séries suivantes — seules celles correspondant à vos équipements apparaissent :</p>
        <ul>
          <li><strong>Charge de base</strong> — la consommation fixe qui doit être satisfaite chaque heure.</li>
          <li><strong>Charge flexible</strong> — la consommation que l'optimiseur est autorisé à décaler dans le temps.</li>
          <li><strong>Production PV</strong> (communauté) ou <strong>PV en toiture</strong> (par bâtiment).</li>
          <li><strong>Allocation de la production communautaire</strong> — part de la production communautaire acheminée vers ce bâtiment (vue par bâtiment uniquement).</li>
          <li><strong>Production éolienne</strong> — production de toute éolienne communautaire.</li>
          <li><strong>Importation depuis le réseau</strong> — énergie prélevée sur le réseau cette heure-là.</li>
          <li><strong>Surplus</strong> — production qui n'a été ni consommée ni stockée cette heure-là (exportée ou écrêtée).</li>
          <li><strong>Charge batterie</strong> et <strong>Décharge batterie</strong> — la contribution de la batterie par heure.</li>
        </ul>

        <h5 className="mt-3">Schémas révélateurs</h5>
        <ul>
          <li><strong>Production PV élevée en milieu de journée + bloc Surplus</strong> — le solaire a produit plus que la communauté ne pouvait consommer ; sans une batterie plus grande, le reste est exporté ou écrêté.</li>
          <li><strong>Production PV à midi + Charge batterie à la même heure</strong> — l'optimiseur économise le surplus pour le pic du soir.</li>
          <li><strong>Décharge batterie le soir + Importation depuis le réseau faible</strong> — la batterie joue son rôle ; peu d'énergie réseau coûteuse est nécessaire aux heures de pointe.</li>
          <li><strong>Importation depuis le réseau la nuit + Charge batterie simultanée</strong> — judicieux uniquement si les tarifs nocturnes sont très bon marché ; sinon, vérifiez le fichier tarifaire.</li>
        </ul>
        <Tip>Recoupez avec la <ArticleLink to="results-battery">courbe d'état de charge de la batterie</ArticleLink> — elles racontent la même histoire sous deux angles différents.</Tip>

        <h5 className="mt-3">Flux Énergétiques Annuels (Planification à Long Terme)</h5>
        <p>
          Un graphique différent, portant un nom similaire, apparaît dans les résultats de la
          <em> Planification à Long Terme</em> — le graphique en barres <strong>Flux Énergétiques
          Annuels</strong>. Chaque barre y représente une année de la projection : les valeurs
          positives correspondent à l'énergie consommée ou importée, les valeurs négatives à
          l'énergie exportée vers le réseau. Utilisez-le pour voir comment le mix énergétique évolue
          sur la durée de vie de l'investissement.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-battery',
    category: 'results',
    title: 'Lire les résultats de la batterie',
    summary: 'Ce que la courbe d\'état de charge et les séries de charge/décharge batterie vous indiquent.',
    body: (
      <>
        <p>
          Lorsqu'une batterie fait partie de votre communauté, l'optimiseur produit une vue
          {' '}<strong>Programme Batterie</strong> avec la <strong>courbe d'état de charge (SOC)
          </strong> indiquant le niveau de charge de la batterie à chaque heure, ainsi que les
          séries de charge et décharge correspondantes dans le graphique{' '}
          <ArticleLink to="results-energy-flow">Bilan Énergétique</ArticleLink>.
        </p>
        <ScreenshotPlaceholder
          img="battery-soc.png"
          description="The state-of-charge curve — 0% to 100% on the Y axis, hours on the X axis, typically a daily up-down pattern between the configured Min SOC and Max SOC bounds (defaults 10% and 90%)."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run on a community that has a battery asset. Scroll to the Battery Schedule section and capture the SOC line chart."
          caption="État de charge sur la journée optimisée."
        />

        <h5 className="mt-3">Un profil journalier sain</h5>
        <ul>
          <li>Le SOC monte pendant les heures ensoleillées (charge depuis PV) ou pendant les heures à tarif bas.</li>
          <li>Le SOC descend lors du pic du soir (décharge vers les charges, évitant les importations réseau coûteuses).</li>
          <li>Le SOC reste dans les plages configurées — les valeurs par défaut sont <strong>Min 10%</strong> et
            <strong> Max 90%</strong>, définies sur l'équipement batterie.</li>
          <li>La courbe ne doit pas rester à plat au minimum ou au maximum pendant de nombreuses heures — cela
            suggère que la batterie est soit trop petite, soit sous-utilisée.</li>
        </ul>

        <h5 className="mt-3">Paramètres de la batterie à vérifier (sur l'équipement)</h5>
        <p>
          Le comportement de la batterie lors de l'optimisation est défini par ces paramètres sur la
          page d'équipement de la communauté ou du bâtiment :
        </p>
        <ul>
          <li><strong>Capacité (kWh)</strong> — quantité d'énergie stockable.</li>
          <li><strong>Vitesse de charge / décharge (kW)</strong> — rapidité avec laquelle elle peut faire circuler l'énergie.</li>
          <li><strong>Rendement aller-retour (%)</strong> — énergie restituée divisée par énergie injectée. La valeur
            par défaut est 90%. Un Li-ion réel typique est à 85–95% — une valeur nettement inférieure signifie que
            les pertes sont modélisées de façon agressive.</li>
          <li><strong>SOC Min / Max (%)</strong> — la plage de fonctionnement, 10% à 90% par défaut.</li>
          <li><strong>SOC Initial (%)</strong> — niveau de départ de la batterie (50% par défaut).</li>
        </ul>

        <h5 className="mt-3">Lire le programme en parallèle avec le SOC</h5>
        <p>
          Comparez la courbe SOC avec les séries Charge batterie / Décharge batterie dans le
          {' '}<ArticleLink to="results-energy-flow">graphique Bilan Énergétique</ArticleLink> : chaque heure
          de « charge » devrait faire monter le SOC, chaque heure de « décharge » devrait le faire
          descendre. Un saut du SOC sans charge correspondante dans le programme signifie que les
          données ont été modifiées entre les vues et ne devrait pas se produire lors d'une exécution unique.
        </p>
        <Warn>
          Une batterie qui reste à 100% (ou à son SOC Max) pendant de nombreuses heures ne fait rien
          — soit le PV est surdimensionné par rapport à la charge, soit le tarif ne présente pas
          d'écart de prix utile, soit l'objectif/les contraintes empêchent la décharge. Essayez
          l'objectif <em>Minimiser le coût</em> avec un tarif Day-Ahead réel pour voir si la batterie
          devient plus active.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-cba',
    category: 'results',
    title: 'Lire les résultats de la Planification à Long Terme (CBA)',
    summary: 'NPV, BCR, retour sur investissement, économies annuelles, CO₂ — ce que signifie chaque chiffre et comment comparer les scénarios.',
    body: (
      <>
        <p>
          La <strong>Planification à Long Terme</strong> compare un ou plusieurs scénarios
          d'investissement (nouveau PV, nouvelle batterie, nouvelles bornes EV, etc.) par rapport à
          la <em>référence</em> du statu quo, sur un horizon que vous choisissez (généralement 5, 10
          ou 20 ans). Le résultat est un tableau comparatif de KPI financiers accompagné de graphiques.
        </p>
        <ScreenshotPlaceholder
          img="cba-scenarios.png"
          description="The CBA scenario comparison — one row per scenario showing NPV, BCR, payback, annual savings, and CO₂ avoided per year. The best-financial scenario is highlighted, and the best-environmental scenario (if different) is badged."
          source="Platform — open a community → click 'Past Results' (or Planning Tools) → run a Long-Term Planning scenario → open its results. Capture the scenario comparison panel near the top of the page."
          caption="Comparaison de scénarios issue de la Planification à Long Terme."
        />

        <h5 className="mt-3">Ce que signifie chaque colonne</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonne</th><th>Signification en langage courant</th><th>Favorable si…</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>NPV</strong></td><td>Bénéfice total (en valeur actuelle) sur la durée de vie du projet, après application du taux d'actualisation.</td><td>Positif (et le plus élevé possible).</td></tr>
            <tr><td><strong>BCR</strong></td><td>Ratio Bénéfice-Coût : total des bénéfices divisé par le total des coûts.</td><td>Supérieur à 1,0.</td></tr>
            <tr><td><strong>Retour sur investissement (années)</strong></td><td>Nombre d'années avant que les économies cumulées couvrent le coût initial.</td><td>Plus court que la durée de vie de l'équipement (ex. le solaire dure 20–25 ans).</td></tr>
            <tr><td><strong>Économies annuelles</strong></td><td>Économies moyennes par an par rapport à la référence (ou coût réseau annuel pour la ligne de référence).</td><td>Plus c'est élevé, mieux c'est — mais à comparer avec le coût initial.</td></tr>
            <tr><td><strong>CO₂ évité / an</strong></td><td>Tonnes de CO₂ non émises par an par rapport à la référence (utilise le facteur CO₂ réseau de votre communauté).</td><td>Plus c'est élevé, mieux c'est ; utile pour les rapports de durabilité.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Note sur le taux d'actualisation</h5>
        <p>
          La page expose un champ <strong>Taux d'actualisation</strong> (8% par défaut) et un
          tableau de sensibilité. Le NPV est calculé pour le taux que vous définissez. Essayez de le
          faire varier de ±2 points de pourcentage pour voir dans quelle mesure votre conclusion est
          sensible — consultez le tableau de <em>sensibilité au taux d'actualisation</em> sur la
          même page.
        </p>

        <h5 className="mt-3">Comment comparer les scénarios</h5>
        <ol>
          <li>Regardez d'abord le <strong>NPV</strong> — c'est le meilleur résumé financier. Le scénario avec le NPV le plus élevé est le gagnant financier et est mis en évidence sur la page.</li>
          <li>Vérifiez avec le <strong>BCR</strong> et le <strong>retour sur investissement</strong> — si le NPV est élevé mais que le retour est de 18 ans pour un équipement qui dure 20 ans, le cas est fragile face aux hypothèses.</li>
          <li>Si deux scénarios sont à égalité sur le NPV, privilégiez le BCR le plus élevé ou le coût initial le plus bas.</li>
          <li>Comparez le <strong>CO2 évité</strong> séparément — le meilleur scénario environnemental peut différer du meilleur scénario financier, et la page le signale en conséquence.</li>
        </ol>

        <Warn>
          Un NPV positif dépend des hypothèses d'entrée — inflation du prix de l'électricité, taux
          d'actualisation, durée de vie des équipements. Utilisez le tableau de sensibilité sur la
          page pour vérifier si le NPV reste positif sous une modification modeste du taux
          d'actualisation ; s'il bascule facilement en négatif, le cas n'est pas robuste.
        </Warn>
        <Tip>
          La page de résultats de la plateforme affiche les meilleurs scénarios financier et
          environnemental sous forme de résumés mis en avant. Utilisez-les pour une lecture rapide,
          mais inspectez toujours le tableau complet avant de recommander un investissement.
        </Tip>
      </>
    ),
  },
]
